import React, { useRef, useEffect, useMemo } from "react";
import { InstancedMesh, Object3D } from "three";
import { Edges } from "@react-three/drei";
import { MM, C_BORDO_MED, C_BORDO_CHIARO } from "./constants";

const C_ALLUMINIO = "#b8c0c8";
const C_FORO      = "#1a1a1a";
const BORDO_TELAIO = MM(60);

export type PannelloSuperficieAerataProps = {
  larghezza: number;
  altezza: number;
  spessore: number;
  tipo: "ft" | "fq";
  onRemove: () => void;
  isDoorClosed: boolean;
  colore?: string;
};

export const PannelloSuperficieAerata: React.FC<PannelloSuperficieAerataProps> = ({
  larghezza,
  altezza,
  spessore,
  tipo,
  onRemove,
  isDoorClosed,
  colore,
}) => {
  const bodyColor  = colore ?? C_ALLUMINIO;
  const frameColor = colore ?? "#8a9099";
  const handleClick = (e: any) => { if (isDoorClosed) { e.stopPropagation(); onRemove(); } };
  const handleOver  = (e: any) => { if (isDoorClosed) { e.stopPropagation(); document.body.style.cursor = "pointer"; } };
  const handleOut   = ()       => { document.body.style.cursor = "default"; };

  const innerW = larghezza - BORDO_TELAIO * 2;
  const innerH = altezza   - BORDO_TELAIO * 2;

  const { positions, holeW, holeH } = useMemo(() => {
    if (tipo === "fq") {
      const hw    = MM(40);
      const hh    = MM(40);
      const pitch = MM(100);
      const cols  = Math.max(1, Math.floor((innerW + pitch - hw) / pitch));
      const rows  = Math.max(1, Math.floor((innerH + pitch - hh) / pitch));
      const gapX  = (innerW - cols * hw) / (cols + 1);
      const gapY  = (innerH - rows * hh) / (rows + 1);
      const pts: [number, number][] = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          pts.push([
            -innerW / 2 + gapX + hw / 2 + c * (hw + gapX),
            -innerH / 2 + gapY + hh / 2 + r * (hh + gapY),
          ]);
        }
      }
      return { positions: pts, holeW: hw, holeH: hh };
    } else {
      // fori tondi: rappresentazione semplificata ingrandita per visibilità
      const dia   = MM(28);
      const pitch = MM(55);
      const cols  = Math.max(1, Math.floor((innerW + pitch - dia) / pitch));
      const rows  = Math.max(1, Math.floor((innerH + pitch - dia) / pitch));
      const gapX  = (innerW - cols * dia) / (cols + 1);
      const gapY  = (innerH - rows * dia) / (rows + 1);
      const pts: [number, number][] = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          pts.push([
            -innerW / 2 + gapX + dia / 2 + c * (dia + gapX),
            -innerH / 2 + gapY + dia / 2 + r * (dia + gapY),
          ]);
        }
      }
      return { positions: pts, holeW: dia, holeH: dia };
    }
  }, [innerW, innerH, tipo]);

  const instancedRef = useRef<InstancedMesh>(null);

  useEffect(() => {
    if (!instancedRef.current) return;
    const dummy = new Object3D();
    positions.forEach(([x, y], i) => {
      dummy.position.set(x, y, spessore / 2 + 0.001);
      dummy.rotation.set(tipo === "ft" ? Math.PI / 2 : 0, 0, 0);
      dummy.updateMatrix();
      instancedRef.current!.setMatrixAt(i, dummy.matrix);
    });
    instancedRef.current.instanceMatrix.needsUpdate = true;
  }, [positions, spessore, tipo]);

  return (
    <group position={[0, 0, -spessore / 2]}>
      {/* Corpo principale */}
      <mesh onClick={handleClick} onPointerOver={handleOver} onPointerOut={handleOut}>
        <boxGeometry args={[larghezza, altezza, spessore]} />
        <meshStandardMaterial color={bodyColor} metalness={0.65} roughness={0.3} />
        <Edges color={C_BORDO_MED} />
      </mesh>

      {/* Telaio – bordo superiore */}
      <mesh position={[0, altezza / 2 - BORDO_TELAIO / 2, spessore / 2 + MM(2)]}>
        <boxGeometry args={[larghezza, BORDO_TELAIO, MM(4)]} />
        <meshStandardMaterial color={frameColor} metalness={0.7} roughness={0.25} />
        <Edges color={C_BORDO_CHIARO} />
      </mesh>
      {/* Telaio – bordo inferiore */}
      <mesh position={[0, -altezza / 2 + BORDO_TELAIO / 2, spessore / 2 + MM(2)]}>
        <boxGeometry args={[larghezza, BORDO_TELAIO, MM(4)]} />
        <meshStandardMaterial color={frameColor} metalness={0.7} roughness={0.25} />
        <Edges color={C_BORDO_CHIARO} />
      </mesh>
      {/* Telaio – bordo sinistro */}
      <mesh position={[-larghezza / 2 + BORDO_TELAIO / 2, 0, spessore / 2 + MM(2)]}>
        <boxGeometry args={[BORDO_TELAIO, innerH, MM(4)]} />
        <meshStandardMaterial color={frameColor} metalness={0.7} roughness={0.25} />
        <Edges color={C_BORDO_CHIARO} />
      </mesh>
      {/* Telaio – bordo destro */}
      <mesh position={[larghezza / 2 - BORDO_TELAIO / 2, 0, spessore / 2 + MM(2)]}>
        <boxGeometry args={[BORDO_TELAIO, innerH, MM(4)]} />
        <meshStandardMaterial color={frameColor} metalness={0.7} roughness={0.25} />
        <Edges color={C_BORDO_CHIARO} />
      </mesh>

      {/* Fori (instanced per performance) */}
      {positions.length > 0 && (
        <instancedMesh ref={instancedRef} args={[undefined, undefined, positions.length]}>
          {tipo === "ft" ? (
            <cylinderGeometry args={[holeW / 2, holeW / 2, MM(4), 14]} />
          ) : (
            <boxGeometry args={[holeW, holeH, MM(4)]} />
          )}
          <meshStandardMaterial color={C_FORO} roughness={1} metalness={0} />
        </instancedMesh>
      )}
    </group>
  );
};
